import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { X, ArrowRight, Upload, ClipboardCheck } from 'lucide-react';

/**
 * OnboardingPrompts — In-product contextual prompts for the OVO flow.
 * Renders the correct prompt based on the user's current onboarding state.
 */
export function OnboardingPrompts() {
  const [progress, setProgress] = useState(null);
  const [orgCount, setOrgCount] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await api.get('/onboarding/progress');
        setProgress(res.data?.progress);
        setOrgCount(res.data?.org_analysis_count ?? 0);
      } catch { /* no OVO record */ }
    };
    fetchProgress();
  }, []);

  const dismiss = useCallback(async () => {
    setDismissed(true);
    try { await api.post('/onboarding/dismiss-prompt'); } catch {}
  }, []);

  const recordShown = useCallback(async () => {
    try { await api.post('/onboarding/prompt-shown'); } catch {}
  }, []);

  if (!progress || progress.is_completed || dismissed) return null;

  const state = progress.state;
  const promptKey = progress.current_prompt_key;

  // First Bid Prompt — only when no analyses exist
  if (promptKey === 'first_bid_prompt' && state === 'signed_up' && orgCount === 0) {
    return (
      <PromptCard
        testId="ovo-first-bid-prompt"
        title="Run your first bid check"
        body="Enter your bid numbers and get an instant commercial decision — underpricing detection, margin exposure, and a recommended price adjustment."
        primaryLabel="Run My First Bid Check"
        primaryAction={() => { recordShown(); navigate('/app/bids/new'); }}
        secondaryLabel="Maybe Later"
        secondaryAction={dismiss}
      />
    );
  }

  // Historical Upload Prompt
  if (promptKey === 'historical_prompt' && (state === 'first_bid_completed' || state === 'historical_partial')) {
    const count = progress.historical_bids_uploaded_count || 0;
    return (
      <PromptCard
        testId="ovo-historical-prompt"
        icon={<Upload className="w-5 h-5 text-orange-500" />}
        title="Add past bids to sharpen future recommendations"
        body={`${Math.max(0, 3 - count)} more past bid${3 - count !== 1 ? 's' : ''} will strengthen your commercial benchmark. Won and lost bids both improve the accuracy of future recommendations.`}
        helper="Start with 3. The more you add, the sharper your checks become."
        primaryLabel="Upload Past Bids"
        primaryAction={() => { recordShown(); navigate('/app/upload'); }}
        secondaryLabel="Skip For Now"
        secondaryAction={dismiss}
      />
    );
  }

  // Outcome Prompt
  if (promptKey === 'outcome_prompt' && state === 'outcome_pending') {
    return (
      <PromptCard
        testId="ovo-outcome-prompt"
        icon={<ClipboardCheck className="w-5 h-5 text-orange-500" />}
        title="Record the outcome to improve your commercial benchmark"
        body="Did a recent bid project finish? A quick win/loss update helps improve the accuracy of your future bid checks."
        primaryLabel="Update Outcome"
        primaryAction={() => { recordShown(); navigate('/app/bids'); }}
        secondaryLabel="Maybe Later"
        secondaryAction={dismiss}
      />
    );
  }

  return null;
}


function PromptCard({ testId, icon, title, body, helper, primaryLabel, primaryAction, secondaryLabel, secondaryAction }) {
  return (
    <Card className="border-orange-200/60 bg-orange-50/30 shadow-sm" data-testid={testId}>
      <CardContent className="py-5 px-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {icon || <ArrowRight className="w-5 h-5 text-orange-500" />}
              <h3 className="text-sm font-bold text-slate-800">{title}</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4 leading-relaxed">{body}</p>
            {helper && <p className="text-xs text-slate-400 mb-4">{helper}</p>}
            <div className="flex items-center gap-3">
              <Button
                onClick={primaryAction}
                className="bg-orange-500 hover:bg-orange-600 text-white text-sm h-9 px-4"
                data-testid={`${testId}-primary-btn`}
              >
                {primaryLabel}
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              {secondaryLabel && (
                <button
                  onClick={secondaryAction}
                  className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid={`${testId}-secondary-btn`}
                >
                  {secondaryLabel}
                </button>
              )}
            </div>
          </div>
          <button
            onClick={secondaryAction}
            className="text-slate-300 hover:text-slate-500 transition-colors shrink-0"
            data-testid={`${testId}-close-btn`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
